import {
  Avatar,
  Button,
  Col,
  Divider,
  Drawer,
  List,
  Popover,
  Row,
  Spin,
} from "antd";
import { green, red } from "@ant-design/colors";

import { useAuthContext } from "../../context/AuthContext";
import { usePaginatedUserNotification } from "../../apis/user";
import { GetError } from "../../widgets/GetError";
import { UserNotification } from "../../types/user";
import {
  CalendarOutlined,
  CaretDownOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { formatToDateAndHour } from "../Utils/TimeUtils";
import { useSearchTags } from "../../utils/useSearchTags";
import { ListHeader } from "../Utils/ListHeader";
import { useEffect, useState } from "react";
import { filterBySearchValueAndTag } from "../Utils/search";
import { flatten } from "lodash";
import { DatePickerForm } from "../Utils/DatePickerForm";

type Props = {
  visibleActivity?: boolean;
  closeDrawer?: () => void;
  id?: string;
};

export const UserNotificationModal = ({
  visibleActivity,
  closeDrawer,
  id,
}: Props) => {
  const { env, token } = useAuthContext();
  const searchTags = useSearchTags("notifications");
  const { tags, searchValue } = searchTags;
  const [filteredData, setFilteredData] = useState<UserNotification[]>([]);
  const [formDates, setFormDates] = useState<Date[]>([]);
  const [updating, setUpdating] = useState(false);
  const notification = usePaginatedUserNotification({
    env,
    token: token[env],
    pageSize: 20,
    start: !!formDates.length ? formDates[0] : undefined,
    end: !!formDates.length ? formDates[1] : undefined,
    id,
  });

  useEffect(() => {
    if (updating) {
      notification.refetch();
      setUpdating(false);
    }
  }, [notification, updating]);
  useEffect(() => {
    if (!notification.data || !notification.dataUpdatedAt) return;
    const searchFields = ["subject", "message", "channel"];

    let filteredNotifications = [...(flatten(notification.data.pages) || [])];
    if (tags.length || searchValue)
      filteredNotifications = filterBySearchValueAndTag<UserNotification>({
        tags,
        searchValue,
        searchFields,
        dataArray: filteredNotifications,
      });

    setFilteredData(filteredNotifications);
  }, [notification.data, notification.dataUpdatedAt, searchValue, tags]);

  if (notification.isLoading) return <Spin />;

  if (!!notification.error || !notification.data)
    return <GetError reFetch={notification.refetch} />;

  return (
    <Drawer
      width="720"
      className="drawer"
      title={<strong>User Notifications</strong>}
      placement="right"
      onClose={closeDrawer}
      visible={visibleActivity}
      key="ActivityFeed"
    >
      <Row gutter={8}>
        <Col>
          <Popover
            placement="bottomLeft"
            trigger="click"
            title="Filter by date"
            content={
              <DatePickerForm
                formDate={formDates}
                setFormDate={setFormDates}
                setIsUpdating={setUpdating}
              />
            }
          >
            <Button icon={<CaretDownOutlined />}>Date</Button>
          </Popover>
        </Col>
        <Col flex="auto">
          <ListHeader
            total={flatten(notification.data.pages).length || 0}
            displayedNum={filteredData.length}
            searchTags={searchTags}
          />
        </Col>
      </Row>
      <List
        itemLayout="horizontal"
        dataSource={filteredData}
        renderItem={(item, index) => (
          <List.Item>
            <List.Item.Meta
              avatar={
                item.successful ? (
                  <Avatar
                    size="small"
                    style={{ backgroundColor: green.primary }}
                    icon={<CheckOutlined />}
                  />
                ) : (
                  <Avatar
                    size="small"
                    style={{ backgroundColor: red.primary }}
                    icon={<CloseOutlined />}
                  />
                )
              }
              title={item.subject}
              description={
                <>
                  <p>{item.message}</p>
                  <p>
                    <CalendarOutlined />
                    <Divider type="vertical" />
                    {formatToDateAndHour(item.time)}
                    <Divider type="vertical" />
                    <span>
                      Channel: <b>{item.channel}</b>
                    </span>
                  </p>
                </>
              }
            />
          </List.Item>
        )}
      />
      <Row>
        {notification.hasNextPage && (
          <Button
            type="primary"
            block
            onClick={() => notification.fetchNextPage()}
          >
            Load More
          </Button>
        )}
      </Row>
    </Drawer>
  );
};
